import React from 'react';
import dayjs from 'dayjs';
import { Button, Table } from 'react-bootstrap';

import { TypeMaster } from 'models/type/TypeMaster';
import { FaEdit, FaTimesCircle } from 'react-icons/fa';
import { toPascalCase } from 'helpers/utils';

type TypeMasterDisplayKey = Extract<keyof TypeMaster, `display_${string}`>;

const displayColumnKeys: TypeMasterDisplayKey[] = [
    'display_docno',
    'display_duedate',
    'display_paymentdate',
    'display_fiscalyear',
    'display_docdate',
    'display_bp',
    'display_remark',
    'display_approve_type',
    'display_amount',
    'display_currency',
    'display_created_by',
    'display_created_on',
    'display_updated_by',
    'display_updated_on',
    'display_duration',
    'display_channel_code',
    'display_channel_name'
];

export type WorkflowTypeMasterTableProps = {
    workflowTypeConfigs?: TypeMaster[];
    isPending?: boolean;
    onEdit?: (typeMaster: TypeMaster) => void;
    onDelete?: (typeMaster: TypeMaster) => void;
};

export const WorkflowTypeMasterTable = (
    props: WorkflowTypeMasterTableProps
) => {
    const { isPending, workflowTypeConfigs, onEdit, onDelete } = props;

    return (
        <div className="overflow-auto">
            <Table
                className="table-bordered align-middle"
                size="sm"
            >
                <thead>
                    <tr className="table-active thead-light w-full text-center">
                        <th />
                        <th className="nowrap">SID</th>
                        <th className="nowrap">Workflow Type ID</th>
                        <th className="nowrap">Workflow Type Name</th>
                        {displayColumnKeys.map(key => {
                            // @ts-ignore
                            const name = toPascalCase(key);

                            return <th key={`th-${key}`}>{name}</th>;
                        })}
                        <th className="nowrap">Badge Color</th>
                        <th className="nowrap">Created On</th>
                        <th className="nowrap">Created By</th>
                        <th className="nowrap">Updated On</th>
                        <th className="nowrap">Updated By</th>
                    </tr>
                </thead>
                <tbody>
                    {isPending ? (
                        <tr>
                            <td colSpan={99}>
                                <div className="flex items-center justify-center">
                                    <div className="spinner-border text-primary my-2" />
                                </div>
                            </td>
                        </tr>
                    ) : (
                        workflowTypeConfigs?.map(type => {
                            const formattedCreatedOn = type.created_on
                                ? dayjs(
                                    type.created_on,
                                    'YYYYMMDDHHmmss'
                                ).format('DD/MM/YYYY HH:mm น.')
                                : undefined;

                            const formattedUpdatedOn = type.updated_on
                                ? dayjs(
                                    type.updated_on,
                                    'YYYYMMDDHHmmss'
                                ).format('DD/MM/YYYY HH:mm น.')
                                : undefined;

                            return (
                                <tr key={type.workflowtypeid}>
                                    <td className="whitespace-nowrap align-middle">
                                        <Button
                                            size="sm"
                                            variant="outline-info"
                                            onClick={() => onEdit?.(type)}
                                        >
                                            <FaEdit />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            className="ml-1"
                                            onClick={() => onDelete?.(type)}
                                        >
                                            <FaTimesCircle />
                                        </Button>
                                    </td>
                                    <td className="text-center align-middle">
                                        {type.sid}
                                    </td>
                                    <td className="align-middle">
                                        {type.workflowtypeid}
                                    </td>
                                    <td className="align-middle">
                                        {type.workflowtypename}
                                    </td>
                                    {displayColumnKeys.map(key => (
                                        <td
                                            key={`td-${key}`}
                                            className="text-center align-middle"
                                        >
                                            <input
                                                type="checkbox"
                                                readOnly
                                                checked={type[key] === 'Y'}
                                            />
                                        </td>
                                    ))}
                                    <td className="align-middle">
                                        {type.background_color && (
                                            <div
                                                style={{
                                                    backgroundColor:
                                                        type.background_color
                                                }}
                                                className="mx-auto h-[40px] w-[40px] rounded-full border-2 border-neutral-200"
                                            />
                                        )}
                                    </td>
                                    <td className="align-middle">
                                        {formattedCreatedOn}
                                    </td>
                                    <td className="align-middle">
                                        {type.created_by}
                                    </td>
                                    <td className="align-middle">
                                        {formattedUpdatedOn}
                                    </td>
                                    <td className="align-middle">
                                        {type.updated_by}
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </Table>
        </div>
    );
};
